import './tooltip.styles.scss'
import { Text } from 'components/text/text'
import { ReactNode, useCallback, useEffect, useRef, useState, JSX } from 'react'
import { randomId } from '@utils/helpers'

const TOOLTIP_OFFSET = 12
const TOOLTIP_IN_DELAY = 200
const TOOLTIP_OUT_DELAY = 0

export const Tooltip = ({
  children,
  content,
}: {
  children: JSX.Element
  content: string | ReactNode
}): JSX.Element => {
  const id = useRef(randomId())

  const tooltipContent = useRef<HTMLDivElement>(null)
  const tooltipContainer = useRef<HTMLDivElement>(null)

  const [state, setState] = useState<'OPENED' | 'CLOSED'>('CLOSED')

  const timeoutId = useRef(null)

  const mouseMoveHandle = useCallback((event: MouseEvent): void => {
    const rect = tooltipContainer?.current?.getBoundingClientRect()
    const x = rect?.x
    const x2 = rect?.x + rect?.width
    const y = rect?.y
    const y2 = rect?.y + rect?.height

    const isInside =
      event?.x > x && event?.x < x2 && event?.y > y && event?.y < y2

    if (!isInside) {
      removeTooltip()
    }
  }, [])

  const handleContentMouseEnter = (): void => {
    setState('OPENED')
    setTimeout(() => {
      addTooltip()
      document.addEventListener('mousemove', mouseMoveHandle)
    }, TOOLTIP_IN_DELAY)
  }

  const handleContentMouseLeave = (): void => {
    document.removeEventListener('mousemove', mouseMoveHandle)
    setState('CLOSED')
    const id = setTimeout(removeTooltip, TOOLTIP_OUT_DELAY)
    timeoutId.current = id
  }

  const addTooltip = (): void => {
    removeTooltip()
    if (state !== 'CLOSED') return

    const container = tooltipContainer?.current?.getBoundingClientRect()

    const height = window.innerHeight
    const width = window.innerWidth

    const horizontalEdgePercent = 0.15
    const verticalEdgePercent = 0.3
    const scroll = window.scrollY

    const halfContainerHeight = container?.height / 2
    const halfContainerWidth = container?.width / 2

    // Initial values
    let top = container?.top + halfContainerHeight + scroll
    let left = container?.left + container?.width / 2
    let transformX = '-50%'
    let transformY = '-100%'

    const closeToLeft = container?.x < width * horizontalEdgePercent
    const closeToRight = container?.x >= width * (1 - horizontalEdgePercent)
    const closeToTop = container?.y - scroll < height * verticalEdgePercent

    if (closeToLeft) {
      transformX = '0%'
      left -= halfContainerWidth
    } else if (closeToRight) {
      transformX = '-100%'
      left += halfContainerWidth
    }

    if (closeToTop) {
      transformY = '0%'
      top += halfContainerHeight + TOOLTIP_OFFSET
    } else {
      top -= TOOLTIP_OFFSET + halfContainerHeight
    }

    document?.querySelector('body')?.insertAdjacentHTML(
      'beforeend',
      `<div class="tooltip__dialog tooltip-${id?.current}"
        style="
              top: ${top}px;
              left: ${left}px;
              transform: translateX(${transformX}) translateY(${transformY});"
        >${tooltipContent?.current?.innerHTML}</div>`,
    )
  }

  const removeTooltip = (): void => {
    const tooltip = document?.querySelector(`.tooltip-${id?.current}`)
    if (tooltip) tooltip.remove()
  }

  useEffect(() => {
    if (state === 'OPENED') addTooltip()
    return removeTooltip
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      ref={tooltipContainer}
      className='tooltip'
      onMouseEnter={handleContentMouseEnter}
      onMouseLeave={handleContentMouseLeave}
    >
      {children}
      <div className='tooltip__content' ref={tooltipContent}>
        {typeof content === 'string' ? (
          <Text preset='paragraph2' text={content} />
        ) : (
          content
        )}
      </div>
    </div>
  )
}
